// Shared WebSocket protocol types. Imported by backend and frontend.

export interface UserMeta {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  email_verified: boolean;
}

export interface OrgMeta {
  id: string;
  name: string;
  slug: string;
  role: 'owner' | 'admin' | 'member';
}

/** API-key permission scopes the compile API recognises (see api auth/apikey.ts). */
export const API_KEY_SCOPES = [
  { value: 'compile:submit', label: 'Submit builds', description: 'Compile projects via `pure build`' },
  { value: 'compile:conformance', label: 'Conformance runs', description: 'Submit conformance / test compiles' },
  { value: 'source:upload', label: 'Upload compiler source', description: 'Upload a custom compiler tarball' },
] as const;

export type Scope = (typeof API_KEY_SCOPES)[number]['value'];

export interface ApiKeyMeta {
  id: string;
  name: string;
  prefix: string;
  scopes: Scope[];
  created_at: number;
  created_by: string;
  last_used: number | null;
  expires_at: number | null;
  revoked_at: number | null;
}

export interface CredentialMeta {
  id: string;
  name: string;
  provider: 'github' | 'gitlab' | 'aws' | 'google';
  oidc_issuer: string;
  subject_claim: string;
  created_at: number;
  last_used: number | null;
  revoked_at: number | null;
}

export interface MemberMeta {
  user_id: string;
  email: string;
  name: string | null;
  image: string | null;
  role: 'owner' | 'admin' | 'member';
  joined_at: number;
}

export interface InviteMeta {
  id: string;
  email: string;
  role: 'admin' | 'member';
  expires_at: number;
  invited_by: string;
}

export interface JobMeta {
  id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  source_kind: 'tarball' | 'zip' | 'github' | 'gitlab';
  source_summary: string;
  entry: string;
  release: boolean;
  credential_id: string | null;
  api_key_id: string | null;
  created_at: number;
  started_at: number | null;
  finished_at: number | null;
  binary_bytes: number | null;
  binary_sha256: string | null;
}

// ─── Client → Server ───

export type ClientMessage =
  | { type: 'auth.token'; token: string }
  | { type: 'org.select'; org_id: string }
  | { type: 'keys.list' }
  | { type: 'keys.create'; name: string; scopes?: Scope[]; expires_at?: number }
  | { type: 'keys.rotate'; id: string }
  | { type: 'keys.revoke'; id: string }
  | { type: 'keys.delete'; id: string }
  | { type: 'keys.update'; id: string; scopes: Scope[] }
  | { type: 'creds.list' }
  | {
      type: 'creds.create';
      name: string;
      provider: CredentialMeta['provider'];
      oidc_issuer: string;
      subject_claim: string;
    }
  | { type: 'creds.revoke'; id: string }
  | { type: 'members.list' }
  | { type: 'members.invite'; email: string; role: 'admin' | 'member' }
  | { type: 'members.remove'; user_id: string }
  | { type: 'members.role'; user_id: string; role: 'owner' | 'admin' | 'member' }
  | { type: 'invites.list' }
  | { type: 'invites.revoke'; id: string }
  | { type: 'jobs.list'; cursor?: string; filter?: Record<string, unknown> }
  | { type: 'jobs.subscribe'; job_id: string }
  | { type: 'jobs.unsubscribe'; job_id: string };

// ─── Server → Client ───

export type ServerMessage =
  | { type: 'auth.ok'; user: UserMeta; org: OrgMeta; orgs: OrgMeta[] }
  | { type: 'auth.error'; message: string }
  | { type: 'org.switched'; org: OrgMeta }
  | { type: 'keys.state'; keys: ApiKeyMeta[] }
  | { type: 'keys.created'; key: ApiKeyMeta; raw: string }
  | { type: 'keys.rotated'; old_id: string; key: ApiKeyMeta; raw: string }
  | { type: 'keys.revoked'; id: string }
  | { type: 'keys.deleted'; id: string }
  | { type: 'keys.updated'; key: ApiKeyMeta }
  | { type: 'keys.used'; id: string; ts: number }
  | { type: 'creds.state'; credentials: CredentialMeta[] }
  | { type: 'creds.created'; credential: CredentialMeta }
  | { type: 'creds.revoked'; id: string }
  | { type: 'members.state'; members: MemberMeta[] }
  | { type: 'invites.state'; invites: InviteMeta[] }
  | { type: 'invites.revoked'; id: string }
  | { type: 'jobs.page'; jobs: JobMeta[]; cursor: string | null }
  | { type: 'job.log'; job_id: string; line: string }
  | { type: 'job.status'; job_id: string; status: JobMeta['status'] }
  | { type: 'job.done'; job_id: string; download_token: string; expires_at: number }
  | { type: 'error'; req_type: string; message: string };

export type MessageType<T extends { type: string }> = T['type'];
export type MessageOfType<T extends { type: string }, K extends T['type']> = Extract<T, { type: K }>;
